import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  FlaskConical,
  History,
  LayoutGrid,
  Loader2,
  MessageSquareText,
  Palette,
  Redo2,
  Rocket,
  Save,
  Search,
  SlidersHorizontal,
  Sparkles,
  Undo2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  NaraAvatar,
  Sheet,
} from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useActiveTheme } from "@/data/hooks";
import { builder as builderApi } from "@/data/services";
import { cn } from "@/lib/cn";
import { useNaraEngine } from "./useNaraEngine";
import { useBuilder, reviewProposal } from "./useBuilder";
import { PromptComposer } from "./PromptComposer";
import { NaraBlockCard } from "./NaraBlockCard";
import { PreviewCanvas } from "./PreviewCanvas";
import { LivePreview } from "./LivePreview";
import { Inspector, AddBlockMenu } from "./Inspector";
import { TreeOutline } from "./TreeOutline";
import { ProposalCard } from "./ProposalCard";
import { PagePicker } from "./PagePicker";
import { TestTab } from "./TestTab";
import { SeoTab } from "./SeoTab";
import { GeneratePagePanel } from "./GeneratePagePanel";
import { VersionHistory } from "./VersionHistory";
import { DesignControls } from "@/screens/designs/DesignControls";
import { applyLayoutRecipe, moveNodeToParent, unwrapNode } from "@dynamic-landing/layout";
import { findParent, removeNode as removeNodeOp, duplicateNode } from "@/editor/treeOps";
import type { ThemeTokens } from "@/editor/theme/ThemeStyleInjector";
import { inferMode, type MakeMode } from "./makeModes";

/**
 * Make is the AI page builder. It runs in one of two modes, chosen once at
 * mount and kept stable for the session:
 *
 *  - LIVE   — the workspace is backed by real data AND the current project has a
 *             real page. The live website preview is the dominant canvas; a right
 *             sidebar holds tabbed controls (Chat / Design / Edit / Test).
 *  - OFFLINE — no live page. Keeps the polished simulated Nara flow + the mock
 *             block preview exactly as before, so Make always works.
 *
 * `useBuilder().live` is the source of truth. Splitting into two screen
 * components keeps each path's hooks unconditional.
 */
export function MakeScreen() {
  const loc = useLocation();
  // CONTRACT: honor an incoming page id (e.g. from a Library "edit" link) as the
  // initial active page. The builder validates it against the project's pages.
  const initialPageId = (loc.state as { pageId?: string } | null)?.pageId;
  const builder = useBuilder(initialPageId);
  return builder.live ? (
    <LiveMakeScreen builder={builder} />
  ) : (
    <OfflineMakeScreen />
  );
}

type SidebarTab = "chat" | "design" | "edit" | "seo" | "test";

/* ============================ LIVE — real builder ========================== */
function LiveMakeScreen({ builder }: { builder: ReturnType<typeof useBuilder> }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { current, currentProjectId } = useWorkspace();
  const activeTheme = useActiveTheme(currentProjectId);
  const deepLink = (loc.state as { prompt?: string } | null)?.prompt;

  const {
    tree,
    reply,
    streaming,
    proposals,
    messages,
    send,
    applyProposal,
    applyTreeTransform,
    save,
    publish,
    reset,
    page,
    pages,
    activePageId,
    activePage,
    setActivePage,
    reloadTree,
    loadingTree,
    error,
    dirty,
    selectedNodeId,
    selectedNode,
    select,
    setProp,
    addBlock,
    moveBlock,
    removeBlock,
    duplicateBlock,
    copyBlock,
    pasteBlock,
    undo,
    redo,
    canUndo,
    canRedo,
  } = builder;

  const [mode, setMode] = useState<MakeMode>(deepLink ? inferMode(deepLink) : "landing");
  const [input, setInput] = useState(deepLink ?? "");
  // Last prompt actually sent — drives "Ask again" so it no longer depends on the
  // composer still holding the text (the composer clears after each send).
  const [lastPrompt, setLastPrompt] = useState("");
  // Show the conversation thread whenever there are messages. Since history is
  // now persisted per page, switching to a page with history shows it straight
  // away (no need to re-send first).
  const started = messages.length > 0;
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [publishOpen, setPublishOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Multi-selection (⌘/Ctrl-click in the outline) for batch delete/duplicate (#21).
  const [multiSel, setMultiSel] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<SidebarTab>("chat");
  // New-blank-page intent (from the orb / Ask Nara) + save/publish busy flags.
  const qc = useQueryClient();
  const newPageIntent = loc.state as { newPage?: boolean; prompt?: string } | null;
  // A near-empty page (root with ≤1 child) gets the "Generate a full page" panel
  // so a blank canvas becomes a starting point in one step (#12).
  const isNearEmpty = !loadingTree && (tree?.root?.children?.length ?? 0) <= 1;
  const [creatingPage, setCreatingPage] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Latch: mount the Design tab the first time it's opened, then keep it mounted
  // (hidden) so its draft survives tab switches. Mounting lazily (vs. from the
  // start) means the layout→design pairing's initialPresetId is set before mount.
  const [designMounted, setDesignMounted] = useState(false);
  // In-progress design tokens streamed up from the Design tab so the MAIN canvas
  // previews edits live (before "Apply"). Null until the Design tab seeds it.
  const [designDraft, setDesignDraft] = useState<ThemeTokens | null>(null);
  // Layout deep-link from the Layouts gallery: restructure this page + pre-select
  // the paired design. Applied once, after the tree loads (see effect below).
  const layoutDeepLink = loc.state as
    | { recipeId?: string; presetId?: string; applyTokens?: boolean }
    | null;
  const [pairedPresetId, setPairedPresetId] = useState<string | undefined>(undefined);
  const appliedLayoutRef = useRef(false);

  const handleSend = (prompt: string) => {
    setLastPrompt(prompt);
    setInput(""); // clear the composer (also covers programmatic / auto sends)
    setTab("chat");
    send(prompt);
  };

  const handleReset = () => {
    reset();
    setInput("");
    setLastPrompt("");
  };

  // Clicking a block in the preview selects it AND jumps to the Edit tab so the
  // marketer sees that node's props immediately (mirrors v1's selection flow).
  const handleSelect = (id: string | null) => {
    select(id);
    if (id) setTab("edit");
  };

  // Switching pages resets the local conversation UI too (the builder resets its
  // own reply/proposals/selection) so designs never bleed across pages.
  const handleSelectPage = (id: string) => {
    if (id === activePageId) return;
    setActivePage(id);
    setInput("");
    setLastPrompt("");
  };

  // Auto-start once from the incoming deep-link prompt. Skipped when a new blank
  // page is being created — there the prompt is sent AFTER that page's tree loads
  // (see the new-page effect), never against the previously-active page.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (deepLink && !autoStarted.current && !newPageIntent?.newPage) {
      autoStarted.current = true;
      handleSend(deepLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  // ── New blank page (the orb / Ask Nara "start something new" intent) ──
  // Create a real draft page, switch to it, then (if a prompt came along) send it
  // once the new page's tree has loaded. Clears nav state so back/remount never
  // recreates a page. Mirrors PagePicker's proven create→invalidate→select path.
  const newPageRef = useRef(false);
  const createdPageIdRef = useRef<string | null>(null);
  const pendingPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!newPageIntent?.newPage || newPageRef.current || !currentProjectId) return;
    newPageRef.current = true;
    pendingPromptRef.current = newPageIntent.prompt?.trim() || null;
    setCreatingPage(true);
    (async () => {
      try {
        const dom = current.domain.includes(".") ? current.domain : "aigen.solutions";
        const stamp = Date.now().toString(36);
        const created = await builderApi.createPage(currentProjectId, {
          title: "Untitled page",
          slug: `untitled-${stamp}`,
          domain: dom,
        });
        await qc.invalidateQueries({ queryKey: ["pages", currentProjectId] });
        createdPageIdRef.current = created.id;
        setActivePage(created.id);
      } catch {
        // Couldn't create — stay on the current page; the prompt stays prefilled
        // in the composer so the user isn't stranded.
        pendingPromptRef.current = null;
      } finally {
        setCreatingPage(false);
        nav(loc.pathname, { replace: true, state: {} });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPageIntent, currentProjectId]);

  // Once the freshly-created page's tree is loaded, send the pending prompt once.
  useEffect(() => {
    const pid = createdPageIdRef.current;
    if (!pid || activePageId !== pid || loadingTree) return;
    const p = pendingPromptRef.current;
    if (!p) return;
    pendingPromptRef.current = null;
    handleSend(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageId, loadingTree]);

  // Latch the Design tab mounted the first time it's shown.
  useEffect(() => {
    if (tab === "design") setDesignMounted(true);
  }, [tab]);

  // Guard against losing unsaved edits on refresh / tab-close / nav-away. (Note:
  // this is the stopgap; debounced auto-save is the fuller fix — see audit #3.)
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Apply a layout deep-link from the Layouts gallery ONCE, after the page tree
  // has loaded. Restructures the page (undoable), opens the Design tab, and —
  // if "apply paired design" was on — pre-selects that preset so its tokens
  // preview live. Clears the nav state so a remount never re-applies.
  useEffect(() => {
    if (appliedLayoutRef.current) return;
    const rid = layoutDeepLink?.recipeId;
    if (!rid || !tree) return;
    appliedLayoutRef.current = true;
    applyTreeTransform((t) => applyLayoutRecipe(t, rid), `Apply ${rid} layout`);
    setTab("design");
    if (layoutDeepLink?.applyTokens && layoutDeepLink.presetId) {
      setPairedPresetId(layoutDeepLink.presetId);
    }
    nav(loc.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, layoutDeepLink]);

  // ⌘Z / ⌘⇧Z (or ⌘Y) — undo/redo manual edits.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return;
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === "z" && e.shiftKey) || k === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // ⌘D duplicate · ⌘C copy · ⌘V paste the selected block. Skips text fields /
  // inline-edit and real text selections (so native copy still works there).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      if (k !== "c" && k !== "v" && k !== "d") return;
      const target = e.target as HTMLElement | null;
      if (target && (/^(input|textarea|select)$/i.test(target.tagName) || target.isContentEditable)) return;
      if (k === "c" && (window.getSelection()?.toString() ?? "")) return; // real text selection
      if (k === "d") {
        if (selectedNodeId) {
          e.preventDefault();
          duplicateBlock(selectedNodeId);
        }
      } else if (k === "c") {
        if (selectedNodeId) {
          e.preventDefault();
          copyBlock(selectedNodeId);
        }
      } else if (k === "v") {
        e.preventDefault();
        pasteBlock();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedNodeId, duplicateBlock, copyBlock, pasteBlock]);

  const domain =
    page?.domain ?? (current.domain.includes(".") ? current.domain : "aigen.solutions");

  const doPublish = async () => {
    setPublishOpen(false);
    setPublishing(true);
    try {
      await publish();
      nav("/signals");
    } catch {
      /* error surfaced in the conversation via `error` */
    } finally {
      setPublishing(false);
    }
  };

  // Save draft — persists the working tree onto the page version WITHOUT promoting
  // it to prod. Wires useBuilder.save(), which was previously never surfaced.
  const handleSaveDraft = async () => {
    setDraftSaving(true);
    try {
      await save();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch {
      /* error surfaced in the conversation via `error` */
    } finally {
      setDraftSaving(false);
    }
  };

  // QUIETER CHAT: a proposal is "actionable" only when at least one of its ops is
  // valid against the current tree. Empty/no-op proposals (e.g. Nara replying to
  // "hi") are NOT rendered as Apply cards — the conversational reply stands alone.
  const realProposals = proposals.filter((p) => reviewProposal(tree, p).some((r) => r.ok));

  // While the Design tab is open, preview the in-progress draft on the real
  // canvas; otherwise fall back to the active (applied) theme.
  const previewTokens =
    tab === "design" && designDraft ? designDraft : (activeTheme.data?.tokens ?? null);

  return (
    <div className="stagger space-y-5 pb-4">
      <Header live />

      {/* SLIM TOOLBAR — page context + undo/redo + the single coral Publish. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-hairline bg-paper px-3 py-2.5 shadow-sm dark:bg-white/[0.03] dark:shadow-none">
        <span className="eyebrow shrink-0">Editing</span>
        <PagePicker pages={pages} activePageId={activePageId} onSelect={handleSelectPage} />
        <div className="ml-auto flex items-center gap-1.5">
          <ToolbarBtn label="Undo (⌘Z)" onClick={undo} disabled={!canUndo}>
            <Undo2 className="h-4 w-4" strokeWidth={1.75} />
          </ToolbarBtn>
          <ToolbarBtn label="Redo (⌘⇧Z)" onClick={redo} disabled={!canRedo}>
            <Redo2 className="h-4 w-4" strokeWidth={1.75} />
          </ToolbarBtn>
          <ToolbarBtn label="Version history" onClick={() => setHistoryOpen(true)} disabled={!activePageId}>
            <History className="h-4 w-4" strokeWidth={1.75} />
          </ToolbarBtn>
          <span className="mx-1 hidden h-5 w-px bg-hairline sm:block" aria-hidden />
          {dirty && !savedFlash && (
            <span className="hidden text-[12px] text-muted sm:inline">Unsaved changes</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveDraft}
            disabled={draftSaving || publishing || !tree || (!dirty && !savedFlash)}
          >
            {draftSaving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                Saving…
              </>
            ) : savedFlash ? (
              <>
                <Check className="h-3.5 w-3.5" strokeWidth={2} />
                Saved
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
                Save draft
              </>
            )}
          </Button>
          <Button
            variant="coral"
            size="sm"
            onClick={() => setPublishOpen(true)}
            disabled={publishing || draftSaving || !tree}
          >
            {publishing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                Publishing…
              </>
            ) : (
              "Publish"
            )}
          </Button>
        </div>
      </div>

      {/* BUILDER — dominant live preview + tabbed right sidebar. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(360px,380px)] lg:items-start">
        {/* LEFT — the live website (the canvas). */}
        <LivePreview
          tree={tree}
          loading={loadingTree || creatingPage}
          error={error}
          device={device}
          onDeviceChange={setDevice}
          domain={domain}
          themeTokens={previewTokens}
          selectedNodeId={selectedNodeId}
          onSelect={handleSelect}
          onEditText={setProp}
        />

        {/* RIGHT — tabbed controls. */}
        <div className="flex flex-col overflow-hidden rounded-card border border-hairline bg-paper dark:bg-white/[0.02] lg:sticky lg:top-4">
          <div className="border-b border-hairline p-2">
            <SidebarTabs tab={tab} onTab={setTab} />
          </div>

          <div className="overflow-y-auto p-4 lg:max-h-[calc(100vh-9.5rem)]">
            {tab === "chat" && (
              <div className="space-y-4">
                <PromptComposer
                  mode={mode}
                  onModeChange={setMode}
                  value={input}
                  onValueChange={setInput}
                  onSubmit={handleSend}
                  onReset={handleReset}
                  streaming={streaming}
                  started={started}
                />

                {started ? (
                  <>
                    {/* conversation thread — user + Nara turns, oldest first */}
                    <div className="space-y-3">
                      {messages.map((m) =>
                        m.role === "user" ? (
                          <div key={m.key} className="flex justify-end">
                            <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent-soft px-3.5 py-2 text-[14px] leading-relaxed text-ink-strong">
                              {m.content}
                            </div>
                          </div>
                        ) : (
                          <div key={m.key} className="flex items-start gap-3">
                            <NaraAvatar size="sm" />
                            <Card className="flex-1 p-3.5">
                              <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-ink">
                                {m.content}
                              </p>
                            </Card>
                          </div>
                        ),
                      )}

                      {/* in-flight reply (committed to the thread when done) */}
                      {streaming && (
                        <div className="flex items-start gap-3">
                          <NaraAvatar size="sm" />
                          <Card className="flex-1 p-3.5">
                            <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-ink">
                              {reply || <span className="text-muted">Thinking…</span>}
                              <span className="ml-0.5 inline-block h-[1.05em] w-[2px] -translate-y-[1px] animate-pulse rounded-full bg-accent align-middle" />
                            </p>
                          </Card>
                        </div>
                      )}
                    </div>

                    {/* actionable proposals for the latest turn (quiet: none when Nara just replies/asks) */}
                    {!streaming && realProposals.length > 0 && (
                      <div className="space-y-2.5">
                        <p className="eyebrow px-0.5">
                          {realProposals.length === 1 ? "1 proposal" : `${realProposals.length} proposals`}
                        </p>
                        {realProposals.map((p, i) => (
                          <ProposalCard key={p.key} proposal={p} tree={tree} index={i} onApply={applyProposal} />
                        ))}
                      </div>
                    )}

                    {error && (
                      <p className="px-0.5 text-[12px] leading-relaxed text-accent">{error}</p>
                    )}

                    {/* action bar */}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => lastPrompt && send(lastPrompt)}
                        disabled={streaming || !lastPrompt}
                      >
                        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Ask again
                      </Button>
                      {dirty && (
                        <span className="ml-auto text-[12px] text-muted">Publish from the toolbar ↑</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {isNearEmpty && (
                      <GeneratePagePanel pageId={activePageId} onApply={applyProposal} disabled={streaming} />
                    )}
                    <Card className="p-5">
                      <div className="flex items-start gap-3">
                        <NaraAvatar size="sm" />
                        <div>
                          <p className="text-[14px] font-medium text-ink-strong">Hi, I'm Nara.</p>
                          <p className="mt-1 text-[13px] leading-relaxed text-muted">
                            I'm live on{" "}
                            <span className="font-medium text-ink">
                              {activePage?.title ?? page?.title ?? "this page"}
                            </span>
                            . Tell me what to change and I'll propose edits to the real page — you
                            review each one, apply it, then publish.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* Design tab stays MOUNTED (just hidden) once opened, so in-progress
                color/font/style edits survive switching tabs — unmounting dropped
                the draft. */}
            {designMounted && (
              <div className={cn("space-y-3", tab !== "design" && "hidden")}>
                <button
                  type="button"
                  onClick={() => nav("/layouts")}
                  className="flex w-full items-center justify-between gap-2 rounded-btn border border-hairline bg-paper-2/50 px-3 py-2.5 text-left transition-colors hover:border-ink/20 dark:bg-white/5"
                >
                  <span className="flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4 text-accent" strokeWidth={1.75} />
                    <span className="text-[13px] font-medium text-ink-strong">Browse layouts</span>
                  </span>
                  <span className="text-[11.5px] text-muted">restructure this page →</span>
                </button>
                <DesignControls
                  key={currentProjectId ?? "none"}
                  variant="panel"
                  onTokensChange={setDesignDraft}
                  onApplyLayout={(recipeId) =>
                    applyTreeTransform((t) => applyLayoutRecipe(t, recipeId), `Apply ${recipeId} layout`)
                  }
                  onUndoLayout={undo}
                  canUndoLayout={canUndo}
                  initialPresetId={pairedPresetId}
                />
              </div>
            )}

            {tab === "edit" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="eyebrow px-0.5">Edit block</p>
                  <AddBlockMenu onAdd={addBlock} />
                </div>
                {multiSel.size > 1 && (
                  <div className="flex items-center gap-2 rounded-btn border border-accent/40 bg-accent-soft/40 px-2.5 py-1.5 text-[12px]">
                    <span className="font-medium text-accent">{multiSel.size} selected</span>
                    <span className="ml-auto flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const ids = Array.from(multiSel);
                          applyTreeTransform(
                            (t) => ids.reduce((acc, id) => duplicateNode(acc, id)?.tree ?? acc, t),
                            `Duplicate ${ids.length} blocks`,
                          );
                          setMultiSel(new Set());
                        }}
                        className="rounded-btn px-2 py-1 font-medium text-ink transition-colors hover:bg-paper-2 dark:hover:bg-white/10"
                      >
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const ids = Array.from(multiSel);
                          applyTreeTransform(
                            (t) => ids.reduce((acc, id) => removeNodeOp(acc, id), t),
                            `Delete ${ids.length} blocks`,
                          );
                          setMultiSel(new Set());
                          if (selectedNodeId && multiSel.has(selectedNodeId)) select(null);
                        }}
                        className="rounded-btn px-2 py-1 font-medium text-accent transition-colors hover:bg-accent/10"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setMultiSel(new Set())}
                        className="rounded-btn px-2 py-1 text-muted transition-colors hover:text-ink"
                      >
                        Clear
                      </button>
                    </span>
                  </div>
                )}
                <TreeOutline
                  tree={tree}
                  selectedNodeId={selectedNodeId}
                  onSelect={(id) => {
                    select(id);
                    setMultiSel(new Set());
                  }}
                  multiSelected={multiSel}
                  onToggleSelect={(id) =>
                    setMultiSel((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    })
                  }
                  onMove={(id, toParentId, index) =>
                    applyTreeTransform((t) => moveNodeToParent(t, id, toParentId, index), "Move block")
                  }
                />
                <Inspector
                  node={selectedNode}
                  rootNodeId={tree?.root.node_id}
                  parentName={
                    tree && selectedNodeId
                      ? (findParent(tree, selectedNodeId)?.component.name ?? undefined)
                      : undefined
                  }
                  onChange={setProp}
                  onMove={moveBlock}
                  onRemove={removeBlock}
                  onDuplicate={duplicateBlock}
                  onUnwrap={(id) => applyTreeTransform((t) => unwrapNode(t, id), "Ungroup")}
                />
              </div>
            )}

            {tab === "seo" && <SeoTab page={page} projectId={currentProjectId} />}

            {tab === "test" && (
              <TestTab pageId={activePageId} tree={tree} selectedNode={selectedNode} />
            )}
          </div>
        </div>
      </div>

      {/* Publish confirmation */}
      <Sheet open={publishOpen} onClose={() => setPublishOpen(false)} title="Publish page">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <p className="text-[14px] leading-relaxed text-muted">
            This saves your edits and publishes{" "}
            <span className="font-medium text-ink">{page?.title ?? "the page"}</span> live to{" "}
            <span className="font-mono text-[12px] text-ink">{domain}</span> right away.
          </p>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setPublishOpen(false)}>
            Cancel
          </Button>
          <Button variant="coral" onClick={doPublish} disabled={publishing}>
            Publish now
          </Button>
        </div>
      </Sheet>

      {/* Version history + restore */}
      <Sheet open={historyOpen} onClose={() => setHistoryOpen(false)} title="Version history">
        {activePageId ? (
          <VersionHistory
            pageId={activePageId}
            currentVersionId={page?.current_version_id}
            onRestored={() => {
              reloadTree();
              setHistoryOpen(false);
            }}
          />
        ) : (
          <p className="text-[13px] text-muted">Pick a page to see its history.</p>
        )}
      </Sheet>
    </div>
  );
}

/* ------------------------------ sidebar tabs ------------------------------ */
function SidebarTabs({ tab, onTab }: { tab: SidebarTab; onTab: (t: SidebarTab) => void }) {
  const tabs = [
    { key: "chat" as const, label: "Chat", Icon: MessageSquareText },
    { key: "design" as const, label: "Design", Icon: Palette },
    { key: "edit" as const, label: "Edit", Icon: SlidersHorizontal },
    { key: "seo" as const, label: "SEO", Icon: Search },
    { key: "test" as const, label: "Test", Icon: FlaskConical },
  ];
  return (
    <div className="flex gap-0.5 rounded-full bg-paper-2 p-1 dark:bg-white/5">
      {tabs.map(({ key, label, Icon }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onTab(key)}
            aria-pressed={active}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-[12px] font-medium transition-colors",
              active
                ? "bg-paper text-ink-strong shadow-sm dark:bg-white/10"
                : "text-muted hover:text-ink",
            )}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ========================== OFFLINE — simulated Nara ======================= */
function OfflineMakeScreen() {
  const loc = useLocation();
  const nav = useNavigate();
  const { current } = useWorkspace();
  const deepLink = (loc.state as { prompt?: string } | null)?.prompt;

  const { streaming, reply, blocks, send, reset, live } = useNaraEngine();
  const [mode, setMode] = useState<MakeMode>(deepLink ? inferMode(deepLink) : "landing");
  const [input, setInput] = useState(deepLink ?? "");
  const [lastPrompt, setLastPrompt] = useState("");
  const [started, setStarted] = useState(false);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  // A/B controls
  const [split, setSplit] = useState(50); // % to variant B
  const [variantPreview, setVariantPreview] = useState<"A" | "B">("B");
  const [launchOpen, setLaunchOpen] = useState(false);

  // publish flow
  const [publishOpen, setPublishOpen] = useState(false);

  const handleSend = (prompt: string) => {
    setStarted(true);
    setLastPrompt(prompt);
    setInput(""); // clear the composer after sending
    send(prompt);
  };

  const handleReset = () => {
    reset();
    setStarted(false);
    setInput("");
    setLastPrompt("");
    setSplit(50);
    setVariantPreview("B");
  };

  // Read the incoming deep-link prompt (the loop) and auto-start once.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (deepLink && !autoStarted.current) {
      autoStarted.current = true;
      handleSend(deepLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const ready = blocks.length > 0 && !streaming;
  const domain = current.domain.includes(".") ? current.domain : "aigen.solutions";

  return (
    <div className="stagger space-y-6 pb-4">
      <Header live={live} />

      {/* composer */}
      <PromptComposer
        mode={mode}
        onModeChange={setMode}
        value={input}
        onValueChange={setInput}
        onSubmit={handleSend}
        onReset={handleReset}
        streaming={streaming}
        started={started}
      />

      {/* conversation ↔ preview */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* LEFT — conversation + blocks */}
        <div className="space-y-3">
          {started ? (
            <>
              {/* Nara's streaming reply */}
              <div className="flex items-start gap-3">
                <NaraAvatar size="sm" />
                <Card className="flex-1 p-4">
                  <p className="text-[14px] leading-relaxed text-ink">
                    {reply || <span className="text-muted">Thinking…</span>}
                    {streaming && (
                      <span className="ml-0.5 inline-block h-[1.05em] w-[2px] -translate-y-[1px] animate-pulse rounded-full bg-accent align-middle" />
                    )}
                  </p>
                </Card>
              </div>

              {/* A/B split control */}
              {mode === "ab" && ready && (
                <SplitControl
                  split={split}
                  onSplit={setSplit}
                  variantPreview={variantPreview}
                  onPreview={setVariantPreview}
                />
              )}

              {/* generated blocks */}
              {blocks.length > 0 && (
                <div className="space-y-2.5">
                  <p className="eyebrow px-0.5">
                    {streaming ? "Drafting blocks…" : `${blocks.length} blocks`}
                  </p>
                  {blocks.map((b, i) => (
                    <NaraBlockCard
                      key={b.id}
                      block={b}
                      index={i}
                      onRegenerate={() => lastPrompt && send(lastPrompt)}
                    />
                  ))}
                </div>
              )}

              {/* action bar — exactly one coral button */}
              {ready && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => lastPrompt && send(lastPrompt)}>
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
                    Regenerate all
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    {mode === "ab" ? (
                      <Button variant="coral" onClick={() => setLaunchOpen(true)}>
                        <Rocket className="h-4 w-4" strokeWidth={1.75} />
                        Launch test
                      </Button>
                    ) : (
                      <Button variant="coral" onClick={() => setPublishOpen(true)}>
                        Publish
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Card className="p-5">
              <div className="flex items-start gap-3">
                <NaraAvatar size="sm" />
                <div>
                  <p className="text-[14px] font-medium text-ink-strong">Hi, I'm Nara.</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-muted">
                    Tell me what to make and I'll draft it block by block. You review each
                    piece, then publish when it feels right. Pick a mode above to start.
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT — live preview */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <PreviewCanvas
            blocks={blocks}
            streaming={streaming}
            mode={mode}
            device={device}
            onDeviceChange={setDevice}
            domain={domain}
            variantLabel={
              mode === "ab"
                ? variantPreview === "A"
                  ? "Variant A · control"
                  : "Variant B · new"
                : undefined
            }
          />
        </div>
      </div>

      {/* Launch A/B test confirmation */}
      <Sheet open={launchOpen} onClose={() => setLaunchOpen(false)} title="Launch A/B test">
        <div className="flex items-start gap-3">
          <NaraAvatar />
          <p className="text-[14px] leading-relaxed text-muted">
            I'll ship variant B and split traffic{" "}
            <span className="font-medium text-ink">
              {100 - split}% / {split}%
            </span>{" "}
            against the control. Results will flow straight back into Signals so you can
            see which one wins.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <SplitStat label="Variant A · control" pct={100 - split} />
          <SplitStat label="Variant B · new" pct={split} focal />
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setLaunchOpen(false)}>
            Not yet
          </Button>
          <Button
            variant="coral"
            onClick={() => {
              setLaunchOpen(false);
              nav("/signals", { state: { panel: "experiments" } });
            }}
          >
            <Rocket className="h-4 w-4" strokeWidth={1.75} />
            Launch &amp; measure
          </Button>
        </div>
        <button
          onClick={() => {
            setLaunchOpen(false);
            nav("/signals");
          }}
          className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
        >
          Or just open Signals
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </Sheet>

      {/* Publish confirmation */}
      <Sheet open={publishOpen} onClose={() => setPublishOpen(false)} title="Publish page">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <p className="text-[14px] leading-relaxed text-muted">
            This will publish {blocks.length} blocks to{" "}
            <span className="font-mono text-[12px] text-ink">{domain}</span>. You can roll
            it back or turn it into an A/B test any time.
          </p>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setPublishOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="coral"
            onClick={() => {
              setPublishOpen(false);
              nav("/signals");
            }}
          >
            Publish now
          </Button>
        </div>
      </Sheet>
    </div>
  );
}

/* ------------------------------ toolbar button ---------------------------- */
function ToolbarBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-8 w-8 place-items-center rounded-btn border border-hairline text-muted transition-colors hover:border-ink/25 hover:text-ink disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/* -------------------------------- header ---------------------------------- */
function Header({ live }: { live: boolean }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex items-center gap-3">
        <NaraAvatar size="lg" />
        <div>
          <p className="eyebrow mb-1">Make</p>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink-strong md:text-[28px]">
            Let's make it together.
          </h1>
        </div>
      </div>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 self-start rounded-full border border-hairline px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide",
          live ? "text-accent" : "text-subtle",
        )}
        title={live ? "Editing the real page via the ai-agent" : "Simulated Nara (no live page)"}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full", live ? "bg-accent" : "bg-subtle")} />
        {live ? "Nara · live" : "Nara · sample"}
      </span>
    </header>
  );
}

/* ----------------------------- A/B split control --------------------------- */
function SplitControl({
  split,
  onSplit,
  variantPreview,
  onPreview,
}: {
  split: number;
  onSplit: (v: number) => void;
  variantPreview: "A" | "B";
  onPreview: (v: "A" | "B") => void;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-muted" strokeWidth={1.5} />
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
            Traffic split
          </h3>
        </div>
        <div className="inline-flex rounded-full border border-hairline p-0.5">
          {(["A", "B"] as const).map((v) => (
            <button
              key={v}
              onClick={() => onPreview(v)}
              className={cn(
                "rounded-full px-3 py-1 text-[12px] font-medium transition-colors",
                variantPreview === v ? "bg-paper-2 text-ink-strong dark:bg-white/10" : "text-muted hover:text-ink",
              )}
            >
              Preview {v}
            </button>
          ))}
        </div>
      </div>

      {/* visual split bar */}
      <div className="flex h-8 overflow-hidden rounded-btn">
        <div
          className="flex items-center justify-center bg-ink-strong/80 text-[11px] font-medium text-canvas transition-[width] duration-200 ease-out-soft dark:bg-white/30"
          style={{ width: `${100 - split}%` }}
        >
          {100 - split >= 14 ? `A ${100 - split}%` : ""}
        </div>
        <div
          className="flex items-center justify-center bg-accent text-[11px] font-medium text-white transition-[width] duration-200 ease-out-soft"
          style={{ width: `${split}%` }}
        >
          {split >= 14 ? `B ${split}%` : ""}
        </div>
      </div>

      <input
        type="range"
        min={10}
        max={90}
        step={5}
        value={split}
        onChange={(e) => onSplit(Number(e.target.value))}
        aria-label="Traffic to variant B"
        className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-paper-2 accent-accent dark:bg-white/10"
      />
      <p className="mt-2 text-[12px] text-muted">
        Sending <span className="font-medium text-ink">{split}%</span> of traffic to the new
        variant.
      </p>
    </Card>
  );
}

function SplitStat({ label, pct, focal }: { label: string; pct: number; focal?: boolean }) {
  return (
    <div className="rounded-card border border-hairline p-3">
      <p className="eyebrow">{label}</p>
      <p
        className={cn(
          "mt-1 text-[24px] font-semibold leading-none tracking-tight",
          focal ? "text-accent" : "text-ink-strong",
        )}
      >
        {pct}%
      </p>
    </div>
  );
}
